 
import React from 'react';

import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';

/**
 * BaseModal Component
 *
 * Standard React Bootstrap Modal wrapper with MetLife branding and consistent styling.
 * This component provides the foundation for all modal dialogs in the application.
 *
 * Features:
 * - Consistent MetLife styling via CSS classes
 * - Support for all Bootstrap Modal props
 * - Size variants (sm, md, lg, xl, compact, wide, full)
 * - Optional centering
 * - Custom class name support
 *
 * Usage:
 * ```jsx
 * <BaseModal
 *   show={show}
 *   size="lg"
 *   centered
 *   onHide={handleClose}
 * >
 *   <Modal.Header closeButton>
 *     <Modal.Title>Title</Modal.Title>
 *   </Modal.Header>
 *   <Modal.Body>Content</Modal.Body>
 *   <Modal.Footer>Footer</Modal.Footer>
 * </BaseModal>
 * ```
 *
 * @param {object} props - Component props
 * @param {boolean} props.show - Show/hide the modal
 * @param {string} props.size - Modal size: 'sm', 'md', 'lg', 'xl', 'compact', 'wide', 'full'
 * @param {boolean} props.centered - Center modal vertically
 * @param {string|boolean} props.backdrop - Backdrop behavior: 'static', true, false
 * @param {boolean} props.keyboard - Enable ESC key to close
 * @param {boolean} props.animation - Enable fade animation
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.dialogClassName - Dialog-specific CSS classes
 * @param {string} props.contentClassName - Content-specific CSS classes
 * @param {function} props.onHide - Close handler (required)
 * @param {function} props.onShow - Show handler
 * @param {function} props.onEnter - Enter animation handler
 * @param {function} props.onEntering - Entering animation handler
 * @param {function} props.onEntered - Entered animation handler
 * @param {function} props.onExit - Exit animation handler
 * @param {function} props.onExiting - Exiting animation handler
 * @param {function} props.onExited - Exited animation handler
 * @param {node} props.children - Modal content
 */
const BaseModal = ({
    show,
    size = 'md',
    centered = false,
    backdrop = true,
    keyboard = true,
    animation = true,
    className = '',
    dialogClassName = '',
    contentClassName = '',
    onHide,
    onShow,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    onExited,
    children,
    ...rest
}) => {
    // Map custom size variants to class names
    const getSizeClass = () => {
        switch (size) {
        case 'compact':
            return 'modal-compact';
        case 'wide':
            return 'modal-wide';
        case 'full':
            return 'modal-full';
        case 'default':
            return 'modal-default';
        default:
            return '';
        }
    };

    // Combine class names
    const modalClasses = [
        className,
        getSizeClass(),
    ].filter(Boolean).join(' ');

    // Convert custom sizes to Bootstrap sizes
    const getBootstrapSize = () => {
        if (['compact', 'wide', 'full', 'default'].includes(size)) {
            return undefined; // Let our CSS classes handle it
        }

        return size; // sm, md, lg, xl
    };

    return (
        <Modal
            show={show}
            size={getBootstrapSize()}
            centered={centered}
            backdrop={backdrop}
            keyboard={keyboard}
            animation={animation}
            className={modalClasses}
            dialogClassName={dialogClassName}
            contentClassName={contentClassName}
            onHide={onHide}
            onShow={onShow}
            onEnter={onEnter}
            onEntering={onEntering}
            onEntered={onEntered}
            onExit={onExit}
            onExiting={onExiting}
            onExited={onExited}
            {...rest}
        >
            {children}
        </Modal>
    );
};

BaseModal.propTypes = {
    // Required props
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,

    // Size options
    size: PropTypes.oneOf([
        'sm',       // Small (Bootstrap default)
        'md',       // Medium (Bootstrap default)
        'lg',       // Large (Bootstrap default)
        'xl',       // Extra large (Bootstrap default)
        'compact',  // Custom: 400px
        'default',  // Custom: 600px
        'wide',     // Custom: 1100px
        'full',     // Custom: 95vw
    ]),

    // Positioning
    centered: PropTypes.bool,

    // Behavior
    backdrop: PropTypes.oneOf(['static', true, false]),
    keyboard: PropTypes.bool,
    animation: PropTypes.bool,

    // Styling
    className: PropTypes.string,
    dialogClassName: PropTypes.string,
    contentClassName: PropTypes.string,

    // Event handlers
    onShow: PropTypes.func,
    onEnter: PropTypes.func,
    onEntering: PropTypes.func,
    onEntered: PropTypes.func,
    onExit: PropTypes.func,
    onExiting: PropTypes.func,
    onExited: PropTypes.func,
};

export default BaseModal;

